import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Hero Section for Blog Page
const BlogHeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#111827] to-[#1F2937]">
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          SwiftBidPro Blog
        </motion.h1>
        
        <motion.p
          className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Insights, tips, and strategies for tradespeople to streamline quoting and grow their business.
        </motion.p>
      </div>
    </section>
  );
};

// Blog List Section (Placeholder)
const BlogListSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  // Placeholder blog posts
  const placeholderPosts = [
    {
      id: 1,
      title: "How to Create Accurate Quotes in Minutes",
      excerpt: "Learn the best practices for creating fast, accurate quotes that win more jobs for your trade business.",
      date: "June 15, 2025",
      category: "Quoting Tips"
    },
    {
      id: 2,
      title: "Top 5 Mistakes Tradespeople Make When Estimating",
      excerpt: "Avoid these common pitfalls to ensure your estimates are professional and profitable.",
      date: "June 10, 2025",
      category: "Business Advice"
    },
    {
      id: 3,
      title: "Using Technology to Streamline Your Trade Business",
      excerpt: "Discover how digital tools like SwiftBidPro can save you hours on paperwork and quoting.",
      date: "June 5, 2025",
      category: "Technology"
    }
  ];

  return (
    <section ref={ref} className="py-20 bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold text-[#111827] mb-4">Latest Articles</h2>
          <p className="text-lg text-[#111827] opacity-70">Stay updated with the latest insights for your trade</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {placeholderPosts.map((post, index) => (
            <motion.div
              key={post.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="p-6">
                <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-[#4F46E5] rounded mb-3">{post.category}</span>
                <h3 className="text-xl font-bold text-[#111827] mb-2">{post.title}</h3>
                <p className="text-[#111827] opacity-70 mb-4">{post.excerpt}</p>
                <p className="text-sm text-gray-500 mb-4">{post.date}</p>
                <Link to={`/blog/${post.id}`} className="text-[#4F46E5] font-semibold hover:underline">Read More →</Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Component (reused from other pages)
const Footer = () => {
  return (
    <footer className="bg-[#111827] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <span className="text-2xl font-bold">SwiftBid Pro</span>
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy-policy" className="hover:text-[#4F46E5] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#4F46E5] transition-colors">Terms of Service</Link>
            <a href="#" className="hover:text-[#4F46E5] transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 text-center text-sm text-gray-400">
          © 2025 SwiftBid Pro. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main Blog Page Component
const BlogPage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>SwiftBidPro Blog | Tips for Tradespeople & Contractors</title>
        <meta name="description" content="Read the latest insights, tips, and strategies for tradespeople to streamline quoting and grow their business with SwiftBidPro." />
        <meta name="keywords" content="contractor blog, tradesperson tips, quoting software blog, estimate software tips" />
      </Helmet>
      <BlogHeroSection />
      <BlogListSection />
      <Footer />
    </div>
  );
};

export default BlogPage;
